import { API_URL, getAuthHeaders } from './config';

export const matchesService = {
    search: async (lat: number, lon: number, radius: number) => {
        const res = await fetch(`${API_URL}/search?lat=${lat}&lon=${lon}&radius=${radius}`);
        if (!res.ok) throw new Error("Failed to search matches");
        return res.json();
    },
    listByClub: async (clubId: string) => {
        const res = await fetch(`${API_URL}/matches?club_id=${clubId}`);
        if (!res.ok) throw new Error("Failed to list matches for club");
        return res.json();
    },
    create: async (data: any) => {
        const res = await fetch(`${API_URL}/matches`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            const msg = errorData.error
                ? (typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error))
                : "Failed to create match";
            const error = new Error(msg);
            (error as any).status = res.status; // Attach status code
            if (errorData.suggestions) {
                (error as any).suggestions = errorData.suggestions;
                (error as any).error = msg; // Redundant but helpful for hook logic
            }
            // Also attach data just in case
            (error as any).data = errorData;
            throw error;
        }
        return res.json();
    },
    update: async (id: number, data: any) => {
        const res = await fetch(`${API_URL}/matches/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            throw new Error("Failed to update match");
        }
        return res.json();
    },
    delete: async (id: number) => {
        const response = await fetch(`${API_URL}/matches/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            }
        });

        if (!response.ok) {
            throw new Error("Failed to delete match");
        }
        return response.json();
    },
    get: async (id: number) => {
        const res = await fetch(`${API_URL}/matches/${id}`);
        if (!res.ok) throw new Error("Failed to fetch match");
        const data = await res.json();

        // Fix relative URLs for players to absolute
        const baseUrl = API_URL.replace(/\/api$/, '');
        if (data.players) {
            data.players = data.players.map((p: any) => {
                if (p.profile_pic_url && p.profile_pic_url.startsWith('/')) {
                    p.profile_pic_url = `${baseUrl}${p.profile_pic_url}`;
                }
                return p;
            });
        }
        return data;
    },
    join: async (id: number, data: string | { user_id: string, payment_intent_id?: string }) => {
        const payload = typeof data === 'string' ? { user_id: data } : data;

        const res = await fetch(`${API_URL}/matches/${id}/join`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(payload)
        });

        if (!res.ok) {
            const err = await res.json() as any;
            throw { message: err.error || "Impossible de rejoindre le match", error: err.error };
        }
        return res.json();
    },
    leave: async (id: number, userId: string) => {
        const res = await fetch(`${API_URL}/matches/${id}/leave`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ user_id: userId })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to leave match");
        }
        return res.json();
    },
    submitScore: async (id: number, teams: any, score: string, winner_team: number) => {
        const res = await fetch(`${API_URL}/matches/${id}/score`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ teams, score, winner_team })
        });
        if (!res.ok) {
            const errText = await res.text();
            throw new Error(errText || "Failed to submit score");
        }
        return res.json();
    },
    submitFeedback: async (id: number, data: any) => {
        const res = await fetch(`${API_URL}/matches/${id}/feedback`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to submit feedback");
        return res.json();
    },
    // Voting
    vote: async (id: number, teamIndex: number, guestId?: string) => {
        const res = await fetch(`${API_URL}/matches/${id}/vote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ team_index: teamIndex, guest_id: guestId })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw { error: err.error || "Failed using voting endpoint" };
        }
        return res.json();
    },
    updateLiveScore: async (id: string | number, data: { action: 'point' | 'undo' | 'start' | 'sanction' | 'start_timer' | 'pause_timer', team?: number, sanctionType?: string }) => {
        const res = await fetch(`${API_URL}/matches/${id}/live-score`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update live score");
        return res.json();
    },
    getVoteStats: async (id: number) => {
        const res = await fetch(`${API_URL}/matches/${id}/vote-stats`);
        if (!res.ok) throw new Error("Failed to get vote stats");
        return res.json();
    },

    setTeam: async (id: number, data: { team: number, userId?: string, guestIndex?: number }) => {
        const res = await fetch(`${API_URL}/matches/${id}/set-team`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to set team");
        }
        return res.json();
    },
    replaceGuest: async (id: number, data: { guestIndex: number, userId: string }) => {
        const res = await fetch(`${API_URL}/matches/${id}/replace-guest`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to replace guest");
        }
        return res.json();
    },
    getAccessDevices: async (id: number) => {
        const res = await fetch(`${API_URL}/matches/${id}/access`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to get access devices");
        return res.json();
    },
    unlockAccessDevice: async (id: number, deviceId: string) => {
        const res = await fetch(`${API_URL}/matches/${id}/access/${deviceId}/unlock`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || "Failed to unlock device");
        }
        return res.json();
    }
};
